"use strict";

var __importDefault = this && this.__importDefault || function (mod) {
  return mod && mod.__esModule ? mod : {
    "default": mod
  };
};
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.transferUserDataForUserPhotos = exports.copyExistingLayers = void 0;
exports.updatePrintableDesignUserPhotoLayers = updatePrintableDesignUserPhotoLayers;
const compareByReadingOrder_1 = __importDefault(require("./compareByReadingOrder"));
const lodash_clonedeep_1 = __importDefault(require("lodash.clonedeep"));
const UserPhotoLayer_1 = require("../../models/layers/UserPhotoLayer");
const getCropForUserPhotoLayer_1 = __importDefault(require("./getCropForUserPhotoLayer"));
const imageCrop_1 = require("../../utils/imageCrop");
/**
 * This function either copies existing photo layers into the new printable's design (shouldCopyExistingUserPhotoLayers === true), or finds the user photo layers in the replacement design and transfers any user data that may have existed to the new layers in reading order and returns the new layers
 * @param oldSurface Old printable surface, needed for determining if the aspect ratio of the well has changed
 * @param newSurface New printable surface, if applicable, needed for re-cropping the image
 * @param workspaceRotatedForPrintable Boolean for whether or not the printable is rotated in the workspace, needed for re-cropping the image, only applicable for prints
 * @param existingPrintableLayers Layers from the old design that may contain user data
 * @param newTemplateLayers Layers for the new design from the new HAMMER template
 * @param shouldCopyExistingUserPhotoLayers Copy layers into the new printable
 * @returns UserPhoto layers from the template filled with prior user data if available
 */
function updatePrintableDesignUserPhotoLayers(_ref) {
  let {
    oldSurface,
    newSurface,
    workspaceRotatedForPrintable,
    existingPrintableLayers,
    newTemplateLayers,
    shouldCopyExistingUserPhotoLayers
  } = _ref;
  const existingUserPhotoLayers = existingPrintableLayers.filter(layer => (0, UserPhotoLayer_1.isUserPhotoLayer)(layer)).sort(compareByReadingOrder_1.default);
  if (shouldCopyExistingUserPhotoLayers) {
    return (0, exports.copyExistingLayers)({
      existingUserPhotoLayers,
      oldSurface,
      newSurface,
      workspaceRotatedForPrintable
    });
  }
  return (0, exports.transferUserDataForUserPhotos)({
    existingUserPhotoLayers,
    newTemplateLayers,
    oldSurface,
    newSurface,
    workspaceRotatedForPrintable
  });
}
const copyExistingLayers = _ref2 => {
  let {
    existingUserPhotoLayers,
    oldSurface,
    newSurface,
    workspaceRotatedForPrintable
  } = _ref2;
  return existingUserPhotoLayers.map(existingLayer => {
    const transferredLayer = Object.assign({}, existingLayer);
    // check aspect ratio
    const oldSurfaceAspectRatio = (0, imageCrop_1.calcAspectRatio)(oldSurface.height, oldSurface.width);
    const newSurfaceAspectRatio = newSurface && (0, imageCrop_1.calcAspectRatio)(newSurface.height, newSurface.width);
    if (existingLayer.userData && newSurface && oldSurfaceAspectRatio.toFixed(2) !== (newSurfaceAspectRatio === null || newSurfaceAspectRatio === void 0 ? void 0 : newSurfaceAspectRatio.toFixed(2))) {
      //prevent image stretching
      transferredLayer.userData = (0, getCropForUserPhotoLayer_1.default)(newSurface || oldSurface, (0, lodash_clonedeep_1.default)(existingLayer), existingLayer.userData.galleryId, existingLayer.userData.galleryImageId, existingLayer.userData.imageData, workspaceRotatedForPrintable, existingLayer.userData.modifications);
    }
    return transferredLayer;
  });
};
exports.copyExistingLayers = copyExistingLayers;
const transferUserDataForUserPhotos = _ref3 => {
  let {
    existingUserPhotoLayers,
    newTemplateLayers,
    oldSurface,
    newSurface,
    workspaceRotatedForPrintable
  } = _ref3;
  const newUserPhotoLayers = newTemplateLayers.filter(layer => (0, UserPhotoLayer_1.isUserPhotoLayer)(layer)).map(layer => (0, lodash_clonedeep_1.default)(layer)) // Ensure we're not modifying the original layer object from the template
  .sort(compareByReadingOrder_1.default); // Ensure that data is transferred in reading order
  newUserPhotoLayers.forEach(newLayer => {
    var _a, _b;
    let oldLayer;
    // if the new layer we're transferring data too is on the right, find the first available old layer also on the right
    if (newLayer.id.includes('--SHIFTED')) {
      oldLayer = existingUserPhotoLayers.find(l => l.id.includes('--SHIFTED'));
    } else {
      oldLayer = existingUserPhotoLayers.find(l => !l.id.includes('--SHIFTED'));
    }
    if (oldLayer) {
      // If an old layer was found, copy the data from the old layer, to the new layer
      if ((0, UserPhotoLayer_1.isFilledUserPhotoLayer)(oldLayer) && oldLayer.userData.imageData.width && oldLayer.userData.imageData.height) {
        // if the aspect ratio of the old layer is the same (or within 10%) as the new layer, we don't need to recalculate the crop
        const oldLayerWidth = oldLayer.width * oldSurface.width;
        const oldLayerHeight = oldLayer.height * oldSurface.height;
        const oldLayerAspectRatio = (0, imageCrop_1.calcAspectRatio)(oldLayerHeight, oldLayerWidth);
        const newLayerWidth = newLayer.width * ((newSurface === null || newSurface === void 0 ? void 0 : newSurface.width) || oldSurface.width);
        const newLayerHeight = newLayer.height * ((newSurface === null || newSurface === void 0 ? void 0 : newSurface.height) || oldSurface.height);
        const newLayerAspectRatio = (0, imageCrop_1.calcAspectRatio)(newLayerHeight, newLayerWidth);
        const aspectRatioDiff = Math.abs(oldLayerAspectRatio - newLayerAspectRatio) / oldLayerAspectRatio;
        if (oldLayerAspectRatio === newLayerAspectRatio) {
          newLayer.userData = oldLayer.userData;
        } else {
          if (aspectRatioDiff < 0.1 && typeof window !== 'undefined' && window.newrelic) {
            // Aspect ratios are within 10%, close enough that the customer might expect the crop to be preserved
            // but not close enough to actually preserve the crop without risking stretching the image
            // For these, we want to report on and keep track of so that we can fix the template spec
            window.newrelic.addPageAction('sdk_user_photo_layer_data_transfer_aspect_ratio_mismatch', {
              oldLayerWidth,
              oldLayerHeight,
              oldLayerAspectRatio,
              newLayerWidth,
              newLayerHeight,
              newLayerAspectRatio,
              aspectRatioDiff,
              oldLayerName: (_a = oldLayer.name) === null || _a === void 0 ? void 0 : _a.split(/_[1|0]\.\d+x/)[0],
              newLayerName: (_b = newLayer.name) === null || _b === void 0 ? void 0 : _b.split(/_[1|0]\.\d+x/)[0]
            });
          }
          newLayer.userData = (0, getCropForUserPhotoLayer_1.default)(newSurface || oldSurface, (0, lodash_clonedeep_1.default)(newLayer), oldLayer.userData.galleryId, oldLayer.userData.galleryImageId, oldLayer.userData.imageData, workspaceRotatedForPrintable, oldLayer.userData.modifications);
        }
      } else {
        newLayer.userData = undefined;
      }
      // then remove it from the list, so we don't transfer the same data multiple times
      const oldLayerId = oldLayer.id;
      existingUserPhotoLayers = existingUserPhotoLayers.filter(l => l.id !== oldLayerId);
    }
  });
  return newUserPhotoLayers;
};
exports.transferUserDataForUserPhotos = transferUserDataForUserPhotos;